import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, Clock, Mail, Download } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { fatouReservationService } from '../../services/fatouReservationService';
import ReservationsManagementTable from './components/ReservationsManagementTable';
import ReservationDetailModal from './components/ReservationDetailModal';
import StatusFilterPanel from './components/StatusFilterPanel';
import BulkActionsPanel from './components/BulkActionsPanel';

export default function AdminFatouReservationsDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    status: null,
    searchQuery: '',
    startDate: null,
    endDate: null,
    confirmationSent: null
  });

  useEffect(() => {
    loadDashboardData();
    loadReservations();

    // Subscribe to real-time updates
    const channel = fatouReservationService?.subscribeToReservations((payload) => {
      if (payload?.eventType === 'INSERT') {
        setReservations(prev => [payload?.new, ...prev]);
        loadDashboardData();
      } else if (payload?.eventType === 'UPDATE') {
        setReservations(prev => prev?.map(r => r?.id === payload?.new?.id ? payload?.new : r));
        loadDashboardData();
      } else if (payload?.eventType === 'DELETE') {
        setReservations(prev => prev?.filter(r => r?.id !== payload?.old?.id));
        loadDashboardData();
      }
    });

    return () => {
      fatouReservationService?.unsubscribe(channel);
    };
  }, []);

  useEffect(() => {
    loadReservations();
  }, [filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const statsData = await fatouReservationService?.getReservationStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReservations = async () => {
    try {
      const data = await fatouReservationService?.getReservations(filters);
      setReservations(data);
    } catch (error) {
      console.error('Error loading reservations:', error);
    }
  };

  const handleUpdateStatus = async (id, status, notes) => {
    try {
      await fatouReservationService?.updateReservationStatus(id, status, notes);
      await loadReservations();
      await loadDashboardData();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    try {
      for (const id of selectedIds) {
        await fatouReservationService?.updateReservationStatus(id, status, `Mise à jour en masse vers ${status}`);
      }
      setSelectedIds([]);
      await loadReservations();
      await loadDashboardData();
    } catch (error) {
      console.error('Error bulk updating status:', error);
      alert('Erreur lors de la mise à jour en masse');
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['Date', 'Client', 'Téléphone', 'Email', 'Collecte', 'Livraison', 'Service', 'Statut']?.join(','),
      ...reservations?.map(r => [
        new Date(r?.created_at)?.toLocaleDateString(),
        r?.customer_name,
        r?.customer_phone,
        r?.customer_email || '',
        r?.pickup_address,
        r?.delivery_address,
        r?.service_type || '',
        r?.status
      ]?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window?.URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a.href = url;
    a.download = `reservations_fatou_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
            <p className="text-gray-600">Chargement du dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Réservations Fatou</h1>
              <p className="text-gray-600">Gestion centralisée des réservations capturées par IA</p>
            </div>
            <button
              onClick={handleExport}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download size={18} />
              <span>Exporter CSV</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Réservations</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalReservations || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">En Attente</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.pendingCount || 0}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="text-yellow-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Confirmées</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.confirmedCount || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Confirmations Envoyées</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.confirmationSentCount || 0}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Mail className="text-purple-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <StatusFilterPanel
          filters={filters}
          onFiltersChange={setFilters}
          stats={stats}
        />

        {/* Bulk Actions */}
        {selectedIds?.length > 0 && (
          <BulkActionsPanel
            selectedCount={selectedIds?.length}
            onBulkStatusUpdate={handleBulkStatusUpdate}
            onClearSelection={() => setSelectedIds([])}
          />
        )}

        {/* Reservations Table */}
        <div className="bg-white rounded-lg shadow">
          <ReservationsManagementTable
            reservations={reservations}
            selectedIds={selectedIds}
            onSelectIds={setSelectedIds}
            onSelectReservation={setSelectedReservation}
            onUpdateStatus={handleUpdateStatus}
            onRefresh={loadReservations}
          />
        </div>

        {/* Detail Modal */}
        {selectedReservation && (
          <ReservationDetailModal
            reservation={selectedReservation}
            onClose={() => setSelectedReservation(null)}
            onUpdateStatus={handleUpdateStatus}
            onRefresh={loadReservations}
          />
        )}
      </div>
    </div>
  );
}